/**
 * 退会手続き: 入力 → 確認 → 完了（静的ビルド用 sessionStorage）
 * 入力 PC Figma 4100-19063 / SP Figma 4100-18729
 * 確認 PC Figma 4100-18754 / SP Figma 4100-18874
 */
import { validateMemberWithdrawalForm, initMemberWithdrawalFormValidation } from './member-withdrawal-form-validation.js';

const STORAGE_KEY = 'takamatsu_member_withdrawal_v1';
const CONFIRM_PATH = '/member_withdrawal_confirm.html';
const COMPLETE_PATH = '/member_withdrawal_complete.html';

/** TODO: デザイン確認用。本番では false にし、データなし時は empty を表示 */
const MEMBER_WITHDRAWAL_CONFIRM_PREVIEW_ALWAYS_VISIBLE = true;

const REASON_LABELS = {
	complete_other: '他社で物件の成約/売却が完了した',
	complete_our: '当社で物件の成約/売却が完了した',
	stop: '物件の購入/売却を中止した',
	other: 'その他',
};

function getForm() {
	return document.querySelector('form[data-member-withdrawal-flow]#member-withdrawal-form');
}

/** 「修正する」で戻ったとき用 */
function collectRawFormFields(form) {
	const fd = new FormData(form);
	const pick = (name) => (fd.get(name) ?? '').toString();
	const type = pick('withdrawal_reason_type');
	return {
		withdrawal_reason_type: type,
		withdrawal_reason_other: pick('withdrawal_reason_other').trim(),
		withdrawal_message: pick('withdrawal_message').trim(),
	};
}

function buildWithdrawalReasonDisplay(fd) {
	const type = (fd.get('withdrawal_reason_type') || '').toString();
	const other = (fd.get('withdrawal_reason_other') || '').toString().trim();
	const base = REASON_LABELS[type];
	let main = base || '—';
	if (type === 'other' && other) {
		main = `その他（${other}）`;
	}
	return main;
}

function collectPayload(form) {
	const fd = new FormData(form);
	const withdrawal_message = (fd.get('withdrawal_message') || '').toString().trim();
	return {
		withdrawal_reason: buildWithdrawalReasonDisplay(fd),
		withdrawal_message,
	};
}

function saveDraft(payload) {
	try {
		sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
	} catch {
		/* ignore */
	}
}

function loadDraft() {
	try {
		const raw = sessionStorage.getItem(STORAGE_KEY);
		if (!raw) return null;
		return JSON.parse(raw);
	} catch {
		return null;
	}
}

function clearDraft() {
	try {
		sessionStorage.removeItem(STORAGE_KEY);
	} catch {
		/* ignore */
	}
}

function applyRestoreToForm(form, raw) {
	if (!raw || typeof raw !== 'object') return;

	if (raw.withdrawal_reason_type != null && String(raw.withdrawal_reason_type) !== '') {
		const v = String(raw.withdrawal_reason_type);
		form.querySelectorAll('input[name="withdrawal_reason_type"]').forEach((rb) => {
			if (rb instanceof HTMLInputElement) rb.checked = rb.value === v;
		});
	}

	const skip = new Set(['withdrawal_reason_type']);
	for (const key of Object.keys(raw)) {
		if (skip.has(key)) continue;
		const val = raw[key];
		const str = val == null ? '' : String(val);
		const el = form.elements.namedItem(key);
		if (!el || el instanceof RadioNodeList) continue;
		if (el.tagName === 'SELECT' || el.tagName === 'TEXTAREA' || el.tagName === 'INPUT') {
			el.value = str;
		}
	}
}

function setText(el, text) {
	if (!el) return;
	el.textContent = text;
}

function initFormPage() {
	const form = getForm();
	if (!form) return;

	initMemberWithdrawalFormValidation(form);

	const draft = loadDraft();
	if (draft && draft._restore && typeof draft._restore === 'object') {
		applyRestoreToForm(form, draft._restore);
	}

	form.addEventListener('submit', (e) => {
		e.preventDefault();
		if (!validateMemberWithdrawalForm(form)) {
			return;
		}
		const payload = collectPayload(form);
		const raw = collectRawFormFields(form);
		saveDraft({ ...payload, _restore: raw });
		window.location.href = CONFIRM_PATH;
	});
}

function initConfirmPage() {
	const page = document.querySelector('[data-member-withdrawal-page="confirm"]');
	if (!page) return;

	const block = document.getElementById('member-withdrawal-confirm-block');
	const empty = document.getElementById('member-withdrawal-confirm-empty');
	const draft = loadDraft();

	if (!block || !empty) return;

	const sendBtn = document.getElementById('member-withdrawal-confirm-send');
	if (sendBtn) {
		sendBtn.addEventListener('click', () => {
			clearDraft();
			window.location.assign(COMPLETE_PATH);
		});
	}

	if (!draft) {
		if (MEMBER_WITHDRAWAL_CONFIRM_PREVIEW_ALWAYS_VISIBLE) {
			block.hidden = false;
			empty.hidden = true;
		} else {
			empty.hidden = false;
			block.hidden = true;
		}
		return;
	}

	const hasReason =
		typeof draft.withdrawal_reason === 'string' && draft.withdrawal_reason.trim().length > 0;

	if (!hasReason) {
		if (MEMBER_WITHDRAWAL_CONFIRM_PREVIEW_ALWAYS_VISIBLE) {
			block.hidden = false;
			empty.hidden = true;
		} else {
			empty.hidden = false;
			block.hidden = true;
		}
		return;
	}

	block.hidden = false;
	empty.hidden = true;

	const reasonEl = document.querySelector('[data-withdrawal-field="withdrawal_reason"]');
	if (reasonEl) setText(reasonEl, String(draft.withdrawal_reason || '—'));

	const messageEl = document.querySelector('[data-withdrawal-field="withdrawal_message"]');
	if (messageEl) {
		const msg =
			typeof draft.withdrawal_message === 'string' && draft.withdrawal_message.trim().length > 0
				? draft.withdrawal_message.trim()
				: '—';
		setText(messageEl, msg);
	}
}

export function initMemberWithdrawalFlow() {
	const page = document.querySelector('[data-member-withdrawal-page]')?.getAttribute('data-member-withdrawal-page');
	if (!page) return;
	if (page === 'form') initFormPage();
	if (page === 'confirm') initConfirmPage();
}
